import { Fragment } from 'react';
import { Report, ReportType } from '@/api/generatedApi';
import { Box, Typography } from '@mui/material';
import { ThresholdRangeDetail } from './details/ThresholdRangeDetail';
import { SearchQueryDetail } from './details/SearchQueryDetail';
import { FolderPathsDetail } from './details/FolderPathsDetail';
import { MetadataFiltersDetail } from './details/MetadataFiltersDetail';
import { FileExtensionsDetail } from './details/FileExtensionsDetail';
import { StyledChip } from './details/SharedStyles';
import { CollapsingChipRow, FilterChip } from './CollapsingChipRow';

const buildFilterChips = (report: Report): FilterChip[] => {
  const hasSearchQuery = Boolean(report.searchQuery?.trim());
  const hasFolderPaths = Boolean(
    report.folderPaths?.length || report.folderIds?.length || report.includeHomeFolderAssets
  );
  const hasMetadataFilters = Object.keys(report.metadataFilters ?? {}).length > 0;
  const hasExtensions = Boolean(report.extensions?.length);
  const excludeAssemblies = Boolean(report.excludeAssemblies);
  const excludeExactDuplicates = Boolean(report.excludeExactDuplicates);
  const hasAnyFilters =
    hasSearchQuery ||
    hasFolderPaths ||
    hasMetadataFilters ||
    hasExtensions ||
    excludeAssemblies ||
    excludeExactDuplicates;

  const candidates: (FilterChip & { isShown: boolean })[] = [
    {
      key: 'thresholds',
      isShown: report.reportType === ReportType.CUSTOM,
      chip: <ThresholdRangeDetail minThreshold={report.minThreshold} maxThreshold={report.maxThreshold} />,
    },
    { key: 'search', isShown: hasSearchQuery, chip: <SearchQueryDetail searchQuery={report.searchQuery} /> },
    {
      key: 'folders',
      isShown: hasFolderPaths,
      chip: (
        <FolderPathsDetail
          folderPaths={report.folderPaths}
          folderIds={report.folderIds}
          includeHomeFolderAssets={report.includeHomeFolderAssets}
        />
      ),
    },
    {
      key: 'metadata',
      isShown: hasMetadataFilters,
      chip: <MetadataFiltersDetail metadataFilters={report.metadataFilters} />,
    },
    { key: 'extensions', isShown: hasExtensions, chip: <FileExtensionsDetail extensions={report.extensions} /> },
    {
      key: 'exclude-assemblies',
      isShown: excludeAssemblies,
      chip: <StyledChip label='Excluding assemblies' size='small' color='default' />,
    },
    {
      key: 'exclude-exact-duplicates',
      isShown: excludeExactDuplicates,
      chip: <StyledChip label='Excluding exact duplicates' size='small' color='default' />,
    },
    {
      key: 'all-assets',
      isShown: !hasAnyFilters && report.reportType !== ReportType.CUSTOM,
      chip: (
        <Typography sx={{ fontSize: '0.813rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>All Assets</Typography>
      ),
    },
  ];

  return candidates.filter(candidate => candidate.isShown);
};

export const ReportDetailsCell = ({ report, noWrap = false }: { report: Report; noWrap?: boolean }) => {
  const chips = buildFilterChips(report);

  if (noWrap) return <CollapsingChipRow chips={chips} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px',
        padding: '4px 0',
        width: '100%',
        flexWrap: 'wrap',
      }}
    >
      {chips.map(({ key, chip }) => (
        <Fragment key={key}>{chip}</Fragment>
      ))}
    </Box>
  );
};
